import { useLocalStorage } from 'usehooks-ts';
import { Link, useLocation } from 'wouter';
import {
  LayoutDashboard, Terminal, Users, Boxes,
  History, ShieldBan, FolderOpen,
  Settings, ScrollText, FileText, Clock,
  TrendingDown, ChevronLeft, ChevronRight, LogOut,
  Images,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useAdminPerms, useExpireAuthData } from '@/hooks/auth';
import { EyeLogo } from '@/components/EyeLogo';

type NavItem = {
  label: string;
  path: string;
  icon: React.ElementType;
  group: 'server' | 'manage' | 'system' | 'insights';
  permission?: string;
};

const NAV: NavItem[] = [
  // SERVER
  { label: 'Dashboard',     path: '/',                      icon: LayoutDashboard, group: 'server' },
  { label: 'Live Console',  path: '/server/console',        icon: Terminal,         group: 'server', permission: 'console.view' },
  { label: 'Resources',     path: '/server/resources',      icon: Boxes,            group: 'server' },
  // MANAGE
  { label: 'Players',       path: '/players',               icon: Users,            group: 'manage' },
  { label: 'File Manager',  path: '/files',                 icon: FolderOpen,       group: 'manage' },
  { label: 'Optimize Textures', path: '/optimize-textures', icon: Images,           group: 'manage' },
  // SYSTEM
  { label: 'Settings',      path: '/settings',              icon: Settings,         group: 'system', permission: 'settings.view' },
  { label: 'Ban Templates', path: '/settings/ban-templates',icon: ShieldBan,        group: 'system' },
  // LOGS
  { label: 'History',       path: '/history',               icon: History,          group: 'logs' },
  { label: 'Console Log',   path: '/system/console-log',    icon: ScrollText,       group: 'logs', permission: 'txadmin.log.view' },
  { label: 'Action Log',    path: '/system/action-log',     icon: FileText,         group: 'logs', permission: 'txadmin.log.view' },
  { label: 'Audit Trail',   path: '/audit',                 icon: Clock,            group: 'logs' },
  { label: 'Player Drops',  path: '/insights/player-drops', icon: TrendingDown,    group: 'logs' },
];

const GROUP_ORDER = ['server', 'manage', 'system', 'logs'] as const;
const GROUP_LABELS: Record<string, string> = {
  server: 'Server', manage: 'Manage', system: 'System', logs: 'Logs',
};

// cyan tint for server+manage, purple tint for system+insights
const isCyan = (group: string) => group === 'server' || group === 'manage';

export function ScSidebar() {
  const [collapsed, setCollapsed] = useLocalStorage('sc-sidebar-collapsed', false);
  const [location] = useLocation();
  const { hasPerm } = useAdminPerms();
  const expireSession = useExpireAuthData();

  const isActive = (path: string) =>
    path === '/' ? location === '/' : location.startsWith(path);

  const handleLogout = () => expireSession('sidebar logout', 'user clicked logout');

  return (
    <aside
      className={cn(
        'relative flex flex-col h-screen sticky top-0 z-30 shrink-0 overflow-hidden',
        'transition-all duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)]',
        collapsed ? 'w-[48px]' : 'w-[240px]',
      )}
      style={{ background: 'linear-gradient(180deg, #090c14 0%, #06080f 100%)' }}
    >
      {/* Right-edge glow line */}
      <div className="absolute right-0 top-[8%] bottom-[8%] w-px opacity-50"
        style={{ background: 'linear-gradient(180deg, transparent, rgba(0,255,209,0.5) 40%, rgba(167,139,250,0.35) 60%, transparent)' }} />

      {/* Grid texture */}
      <div className="absolute inset-0 grid-bg opacity-20 pointer-events-none" />

      {/* ── Header ── */}
      <div className={cn(
        'relative flex items-center h-[56px] shrink-0 border-b overflow-hidden',
        'border-white/[0.05]',
        collapsed ? 'justify-center px-0' : 'px-4 gap-3',
      )}>
        {/* Interactive eye logo */}
        <div className="relative shrink-0 flex items-center justify-center overflow-visible" style={{ width: 44, height: 32 }}>
          <EyeLogo width="44" height="32" />
        </div>

        {!collapsed && (
          <div className="overflow-hidden whitespace-nowrap">
            <div className="text-[12px] font-black tracking-tight text-white uppercase leading-none">
              SC{' '}
              <span style={{ background: 'linear-gradient(90deg,#00ffd1,#a78bfa)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                Labs
              </span>
            </div>
          </div>
        )}
      </div>

      {/* ── Nav ── */}
      <nav className="flex-1 overflow-y-auto py-3 flex flex-col gap-0.5 px-2"
        style={{ scrollbarWidth: 'none' }}>
        {GROUP_ORDER.map((group) => {
          const items = NAV.filter((i) => i.group === group);
          const cyan = isCyan(group);
          return (
            <div key={group} className="mb-2">
              {/* Group label */}
              {!collapsed && (
                <div className="px-2 pb-1 pt-1">
                  <span className="text-[7.5px] font-bold tracking-[0.2em] uppercase"
                    style={{ color: 'rgba(71,85,105,0.8)' }}>
                    {GROUP_LABELS[group]}
                  </span>
                </div>
              )}
              {items.map((item) => {
                if (item.permission && !hasPerm(item.permission)) return null;
                const active = isActive(item.path);
                const Icon = item.icon;

                const accentColor = cyan ? '#00ffd1' : '#a78bfa';
                const accentBg    = cyan ? 'rgba(0,255,209,0.08)' : 'rgba(167,139,250,0.08)';
                const accentBdr   = cyan ? 'rgba(0,255,209,0.14)' : 'rgba(167,139,250,0.14)';

                const btn = (
                  <Link key={item.path} href={item.path} asChild>
                    <a className={cn(
                      'relative flex items-center rounded-xl transition-all duration-150 cursor-pointer select-none',
                      collapsed ? 'h-9 w-9 justify-center mx-auto' : 'h-9 px-3 gap-3',
                      active
                        ? 'border'
                        : 'border border-transparent hover:bg-white/[0.03] hover:border-white/[0.06]',
                    )}
                      style={active ? {
                        background: accentBg,
                        borderColor: accentBdr,
                        color: accentColor,
                      } : { color: 'var(--sc-muted)' }}
                    >
                      {/* Active left pip */}
                      {active && !collapsed && (
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 rounded-r-full"
                          style={{ background: accentColor, boxShadow: `0 0 8px ${accentColor}` }} />
                      )}
                      <Icon size={14} className={cn('shrink-0', active ? '' : 'text-[#475569] group-hover:text-[#94a3b8]')}
                        style={active ? { color: accentColor, filter: `drop-shadow(0 0 4px ${accentColor}88)` } : {}} />
                      {!collapsed && (
                        <span className="text-[11px] font-semibold truncate">
                          {item.label}
                        </span>
                      )}
                    </a>
                  </Link>
                );

                if (collapsed) {
                  return (
                    <Tooltip key={item.path} delayDuration={0}>
                      <TooltipTrigger asChild>{btn}</TooltipTrigger>
                      <TooltipContent side="right" className="text-xs font-semibold">
                        {item.label}
                      </TooltipContent>
                    </Tooltip>
                  );
                }
                return btn;
              })}
            </div>
          );
        })}
      </nav>

      {/* ── Footer ── */}
      <div className={cn(
        'border-t border-white/[0.05] flex flex-col gap-1 p-2',
      )}>
        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed((v) => !v)}
          className={cn(
            'flex items-center rounded-xl h-9 transition-all duration-150',
            'border border-transparent hover:bg-white/[0.04] hover:border-white/[0.06]',
            collapsed ? 'justify-center w-9 mx-auto' : 'px-3 gap-3 w-full',
          )}
          style={{ color: 'var(--sc-muted)' }}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight size={14} /> : (
            <>
              <ChevronLeft size={14} />
              <span className="text-[11px] font-semibold">Collapse</span>
            </>
          )}
        </button>

        {/* Logout */}
        <button
          onClick={handleLogout}
          className={cn(
            'flex items-center rounded-xl h-9 transition-all duration-150',
            'border border-transparent hover:bg-red-500/[0.08] hover:border-red-500/[0.15] hover:text-red-400',
            collapsed ? 'justify-center w-9 mx-auto' : 'px-3 gap-3 w-full',
          )}
          style={{ color: 'var(--sc-muted)' }}
          aria-label="Logout"
        >
          {collapsed ? (
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <LogOut size={14} />
              </TooltipTrigger>
              <TooltipContent side="right">Logout</TooltipContent>
            </Tooltip>
          ) : (
            <>
              <LogOut size={14} />
              <span className="text-[11px] font-semibold">Logout</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
}
